import { Box, Button, Flex, Separator, SvgIcon, Tabs } from '@volue/wave-react';
import React from 'react';

import { SplitView } from '@/common/components/SplitView/SplitView';
import { SplitViewHeader } from '@/common/components/SplitView/SplitViewHeader';
import { SplitViewMap } from '@/common/components/SplitView/CalculationResults.SplitViewMap';
import matchState from '@/common/utils/match';
import type { LoadFlowSummary } from '@/features/execute-calculation-set/overmind/calculation/derived-state/loadFlowSummary';
import type { Context } from '@/overmind';
import { useActions, useAppState } from '@/overmind';

import { hasLoadFlowCalculationDetails } from '../../hasLoadFlowCalculations';
import { isCalculationTacticResultUnavailable } from '../../overmind/calculation/actions';
import type { ShortCircuitSummary } from '../../overmind/calculation/derived-state/shortCircuitSummary';
import { CalculationTacticResults } from './CalculationTacticResults';
import { GridElementCalculationTacticResults } from './GridElementsCalculationTacticResults/GridElementCalculationTacticResults';
import { SelectLoadFlowButton } from './SelectLoadFlowButton';
import { CalculationHeader } from './components/CalculationHeader';
import { TacticResultInMap } from './components/TacticResultLayers';

export type AllTacticResultsProps = {
  calculationTacticResult: Context['state']['calculation']['calculationTactic']['value'];
  loadFlowSummary: LoadFlowSummary;
  shortCircuitSummary: ShortCircuitSummary;
};

export const CalculationTacticResultNavigation: React.FunctionComponent =
  () => {
    const calculationResult =
      useAppState().calculation.calculationTactic.matches('LOADED')?.value;

    const browseTacticResults = useActions().calculation.browseTacticResults;

    const tacticResultBrowsingFilter =
      useAppState().calculation.tacticResultBrowsingFilter;

    const availableCalculationScenarios =
      useAppState().calculation.availableCalculationScenarios;
    const isLoadFlowVisible = hasLoadFlowCalculationDetails(
      availableCalculationScenarios
    );

    const shortCircuitRequiresAttention =
      useAppState().calculation.shortCircuitCalculationRequiresAttention;

    if (!calculationResult) return null;

    return (
      <Tabs value={tacticResultBrowsingFilter.type}>
        <Tabs.List
          css={{ marginBottom: 0 }}
          withDivider={false}
          className="mHm"
        >
          {availableCalculationScenarios.type === 'ALL_AVAILABLE' && (
            <>
              <Tabs.Trigger
                onClick={() => browseTacticResults({ type: 'ALL' })}
                value="ALL"
              >
                {'Calculation set'}
              </Tabs.Trigger>
              <Box
                css={{
                  display: 'inline-flex',
                  height: '3rem',
                  verticalAlign: 'bottom',
                  padding: '$spacingS $spacingL $spacingXs'
                }}
              >
                <Separator orientation="vertical" />
              </Box>
            </>
          )}
          {isLoadFlowVisible && <SelectLoadFlowButton />}
          {((availableCalculationScenarios.type === 'SOME_AVAILABLE' &&
            availableCalculationScenarios.scenarios.has('SHORT_CIRCUIT')) ||
            availableCalculationScenarios.type === 'ALL_AVAILABLE') && (
            <Tabs.Trigger
              onClick={() => browseTacticResults({ type: 'SHORT_CIRCUIT' })}
              value="SHORT_CIRCUIT"
            >
              <Flex alignItems={'center'}>
                {shortCircuitRequiresAttention.type === 'AVAILABLE' &&
                shortCircuitRequiresAttention.requiresAttention ? (
                  <SvgIcon
                    iconName="warning"
                    css={{ color: '$colorOrange80' }}
                  />
                ) : (
                  <SvgIcon
                    iconName="validation"
                    css={{ color: '$colorGreen70' }}
                  />
                )}
                <span>{'Short circuit'}</span>
              </Flex>
            </Tabs.Trigger>
          )}
        </Tabs.List>
      </Tabs>
    );
  };

export function Calculation() {
  const resultsBrowsingMode = useAppState().calculation.resultsBrowsingMode;
  const { showHomeWithLocation } = useActions();
  const tacticResultBrowsingFilter =
    useAppState().calculation.tacticResultBrowsingFilter;

  const close = () => {
    showHomeWithLocation()
    // pageOpened({ page: 'root' });
  };

  const calculationTactic = useAppState().calculation.calculationTactic.value;
  if (isCalculationTacticResultUnavailable(calculationTactic)) return null;
  const voltagelevel = calculationTactic.calculationSetResult.voltageLevel;

  return (
    <Tabs value={tacticResultBrowsingFilter.type}>
      <SplitView
        header={
          <>
            <SplitViewHeader
              actionButtons={[
                <Button
                  leftIcon={<SvgIcon iconName="close" />}
                  variant="outline"
                  size="small"
                  className="mAn"
                  onClick={close}
                >
                  {'Close'}
                </Button>
              ]}
            >
              <CalculationHeader />
            </SplitViewHeader>
            <Separator />
            <CalculationTacticResultNavigation />
          </>
        }
        textPanel={matchState(resultsBrowsingMode, {
          BROWSING_SUMMARY: () => <CalculationTacticResults />,
          BROWSING_GRID_ELEMENT: () => <GridElementCalculationTacticResults />
        })}
        mapPanel={
          <SplitViewMap voltagelevel={voltagelevel}>
            <TacticResultInMap voltageLevel={voltagelevel} />
          </SplitViewMap>
        }
      />
    </Tabs>
  );
}
